/**
 * Foreign Missions (البعثات الخارجية)
 * Data for 2026/2027 academic year — sources:
 *   - Country/specialty list + minAcceptedLastYear: خطة بعثات وزارة التعليم
 *     العالي للعام الجامعي 2026/2027 (202671114559.pdf, July 2026).
 *   - equivalentRate apply-floors: Foreign_Missions.pdf plan tables (unchanged).
 *
 * Hierarchy: Track (scientific/literary) → Country → College (discipline group)
 *            → Specialty. Country order matches the PDF exactly (USA → UAE).
 *
 * Weights policy (per stakeholder decision):
 *   - Medicine, Dentistry → weighted equivalent rate (HS 90% / Math 5% / English 5%)
 *   - Pharmacy → weighted equivalent rate (HS 70% / Math 15% / English 15%)
 *   - All Engineering specialties → weighted equivalent rate
 *       (HS 65% / Math 20% / English 15%)
 *   - Every other specialty → HS-only (no math/english inputs)
 *
 * Two numbers per offering:
 *   - equivalentRate      = HS percentage required to apply (plan floor)
 *   - minAcceptedLastYear = "المعدلات الدنيا للقبول" column of the 2026/2027
 *     plan PDF — the lowest rate accepted in 2025/2026, used as the
 *     competitive comparison target on the results page. Rows flagged
 *     نسبة الثانوية hold a raw HS percentage; rows flagged معدل مكافئ hold a
 *     weighted equivalent rate (matches each specialty's admissionType).
 *
 * Legacy rows: 20 (country × specialty) offerings from the 2025/2026 plan
 * that the 2026/2027 plan dropped are kept selectable (stakeholder request).
 * They are flagged in fm2025LegacyCodes and display with a "(2025/2026)"
 * suffix via the dream_university_fm_2025_2026_* label keys; their floors and
 * minAcceptedLastYear values are the ones from the previous cycle.
 */

import type { College, CollegeWeights, Department, Gender, Scores, StepAlert, StepAlertLine, Track } from "./sharedTypes";
import { getCollegeNameKey } from "./sharedTypes";

// ── FM weight templates (medicine, pharmacy, engineering only) ──────────

/** Medicine & Dentistry share the same weighted formula. */
const fmMedicineDentistry: CollegeWeights = {
    highSchoolWeight: 90,
    arabicWeight: 0,
    chemistryWeight: 0,
    mathWeight: 5,
    englishWeight: 5,
    minHighSchool: null,
    minEnglish: null,
    admissionType: "equivalent",
};

const fmPharmacy: CollegeWeights = {
    highSchoolWeight: 70,
    arabicWeight: 0,
    chemistryWeight: 0,
    mathWeight: 15,
    englishWeight: 15,
    minHighSchool: null,
    minEnglish: null,
    admissionType: "equivalent",
};

const fmEngineering: CollegeWeights = {
    highSchoolWeight: 65,
    arabicWeight: 0,
    chemistryWeight: 0,
    mathWeight: 20,
    englishWeight: 15,
    minHighSchool: null,
    minEnglish: null,
    admissionType: "equivalent",
};

/** Every other specialty (allied medical, sciences, business, arts, …) uses
 *  the HS-only template — no math/english inputs requested from the student. */
const fmHsOnly: CollegeWeights = {
    highSchoolWeight: 100,
    arabicWeight: 0,
    chemistryWeight: 0,
    mathWeight: 0,
    englishWeight: 0,
    minHighSchool: null,
    minEnglish: null,
    admissionType: "high_school",
};

// ── Country list (PDF order) ────────────────────────────────────────────

export interface FmCountry {
    key: string;
    iso: string;
    order: number;
}

export const fmCountries: FmCountry[] = [
    { key: "united_states",  iso: "us", order: 1 },
    { key: "united_kingdom", iso: "gb", order: 2 },
    { key: "ireland",        iso: "ie", order: 3 },
    { key: "australia",      iso: "au", order: 4 },
    { key: "new_zealand",    iso: "nz", order: 5 },
    { key: "malta",          iso: "mt", order: 6 },
    { key: "bahrain",        iso: "bh", order: 7 },
    { key: "saudi_arabia",   iso: "sa", order: 8 },
    { key: "uae",            iso: "ae", order: 9 },
];

export function getFmCountryByKey(countryKey: string): FmCountry | undefined {
    return fmCountries.find((c) => c.key === countryKey);
}

// ── Discipline (college) groupings ─────────────────────────────────────

export type FmDisciplineKey =
    | "medicine_dentistry_pharmacy"
    | "allied_medical"
    | "health_life_sciences"
    | "engineering"
    | "computer_science_it"
    | "pure_sciences"
    | "architecture_design"
    | "business_management"
    | "arts_humanities"
    | "agriculture_food_sciences"
    | "agriculture";

export interface FmDiscipline {
    key: FmDisciplineKey;
    emoji: string;
    order: number;
}

export const fmDisciplines: FmDiscipline[] = [
    { key: "medicine_dentistry_pharmacy", emoji: "🩺", order: 1 },
    { key: "allied_medical",              emoji: "🏥", order: 2 },
    { key: "health_life_sciences",        emoji: "🧬", order: 3 },
    { key: "engineering",                 emoji: "🛠️", order: 4 },
    { key: "computer_science_it",         emoji: "💻", order: 5 },
    { key: "pure_sciences",               emoji: "🔬", order: 6 },
    { key: "architecture_design",         emoji: "🏛️", order: 7 },
    { key: "business_management",         emoji: "💼", order: 8 },
    { key: "arts_humanities",             emoji: "📚", order: 9 },
    { key: "agriculture_food_sciences",   emoji: "🌾", order: 10 },
    { key: "agriculture",                 emoji: "🌱", order: 11 },
];

/** Map specialty (collegeKey in the data) → discipline grouping. */
const specialtyToDiscipline: Record<string, FmDisciplineKey> = {
    // Medicine, Dentistry & Pharmacy
    medicine: "medicine_dentistry_pharmacy",
    dentistry: "medicine_dentistry_pharmacy",
    pharmacology: "medicine_dentistry_pharmacy",
    pharmacy: "medicine_dentistry_pharmacy",

    // Allied medical
    nursing: "allied_medical",
    child_nursing: "allied_medical",
    mental_health_nursing: "allied_medical",
    intellectual_disability_nursing: "allied_medical",
    advanced_therapeutic_tech: "allied_medical",
    physiotherapy: "allied_medical",
    radiography: "allied_medical",
    radiation_therapy: "allied_medical",
    diagnostic_radiology: "allied_medical",
    medical_diagnostic_imaging: "allied_medical",
    radiology_sciences: "allied_medical",
    podiatry: "allied_medical",
    occupational_therapy: "allied_medical",
    speech_therapy: "allied_medical",
    optics: "allied_medical",
    paramedics: "allied_medical",
    paramedic_honours: "allied_medical",
    dental_technology: "allied_medical",
    dental_care: "allied_medical",
    oral_health: "allied_medical",
    veterinary_medicine: "allied_medical",
    respiratory_therapy: "allied_medical",
    emergency_medical_services: "allied_medical",
    biomedical_technology: "allied_medical",
    immunology_pathology: "allied_medical",
    medical_lab_science: "allied_medical",
    clinical_lab_sciences: "allied_medical",
    clinical_nutrition_sa: "allied_medical",
    clinical_nutrition_ae: "allied_medical",

    // Health & Life Sciences
    biomedical_health_life_sciences: "health_life_sciences",
    biomedical_sciences: "health_life_sciences",
    human_health_disease: "health_life_sciences",
    health_performance_science: "health_life_sciences",
    sports_health_sciences: "health_life_sciences",
    community_health_sciences: "health_life_sciences",
    kinesiology: "health_life_sciences",

    // Engineering
    biomedical_engineering: "engineering",
    environmental_engineering: "engineering",
    renewable_energy_engineering: "engineering",
    software_engineering: "engineering",
    materials_engineering: "engineering",
    mechatronics_engineering: "engineering",
    surveying_engineering: "engineering",
    computer_engineering: "engineering",
    computer_network_engineering: "engineering",
    aviation_engineering: "engineering",
    chemical_engineering: "engineering",
    electronics_engineering: "engineering",
    architectural_engineering: "engineering",
    manufacturing_aviation_engineering: "engineering",
    cybersecurity_engineering: "engineering",
    food_processing_engineering: "engineering",

    // Computer Science & IT
    computer_science: "computer_science_it",
    computer_science_cyber_security: "computer_science_it",
    computer_science_ai: "computer_science_it",
    cybersecurity: "computer_science_it",
    cyber_security_it_forensics: "computer_science_it",
    data_science: "computer_science_it",
    artificial_intelligence: "computer_science_it",
    computer_game_dev: "computer_science_it",
    multimedia: "computer_science_it",

    // Pure Sciences
    physics: "pure_sciences",
    chemistry: "pure_sciences",
    biology: "pure_sciences",
    mathematics: "pure_sciences",
    statistics: "pure_sciences",
    geology: "pure_sciences",
    biochemistry: "pure_sciences",
    biochemistry_molecular_biology: "pure_sciences",
    cell_molecular_biology: "pure_sciences",
    microbiology: "pure_sciences",
    marine_biology: "pure_sciences",
    meteorology: "pure_sciences",

    // Architecture & Design
    architecture: "architecture_design",
    architectural_science: "architecture_design",
    landscape_architecture: "architecture_design",
    urban_planning: "architecture_design",
    interior_design: "architecture_design",
    graphic_design: "architecture_design",
    product_design: "architecture_design",

    // Business & Management
    supply_chain_management: "business_management",
    finance: "business_management",
    marketing: "business_management",
    marketing_innovation_tech: "business_management",
    entrepreneurship: "business_management",
    construction_management: "business_management",
    tourism_hospitality: "business_management",
    technology_management: "business_management",
    aviation_management: "business_management",
    actuarial_sciences: "business_management",
    actuarial_financial_studies: "business_management",

    // Arts & Humanities
    english_literature: "arts_humanities",
    special_education: "arts_humanities",

    // Agriculture & Food Sciences
    agriculture_sciences: "agriculture_food_sciences",
    food_science: "agriculture_food_sciences",
    food_human_nutrition: "agriculture_food_sciences",
    nutrition_sciences: "agriculture_food_sciences",
    human_nutrition: "agriculture_food_sciences",
    health_food_sciences: "agriculture_food_sciences",
};

export function getDisciplineForSpecialty(specialtyKey: string): FmDisciplineKey | undefined {
    return specialtyToDiscipline[specialtyKey];
}

/** Country-aware discipline display name. Ireland labels its Health & Life
 *  Sciences grouping "العلوم الصحية والتأهيل الطبي"; every other country uses
 *  the default discipline name. */
export function getFmDisciplineNameKey(disciplineKey: string, countryKey: string): string {
    if (countryKey === "ireland" && disciplineKey === "health_life_sciences") {
        return "dream_university_fm_discipline_health_life_sciences_ie";
    }
    if (countryKey === "australia" && disciplineKey === "allied_medical") {
        return "dream_university_fm_discipline_allied_medical_au";
    }
    if (countryKey === "new_zealand" && disciplineKey === "allied_medical") {
        return "dream_university_fm_discipline_allied_medical_nz";
    }
    if (countryKey === "saudi_arabia" && disciplineKey === "health_life_sciences") {
        return "dream_university_fm_discipline_health_life_sciences_sa";
    }
    if (countryKey === "australia" && disciplineKey === "business_management") {
        return "dream_university_fm_discipline_business_management_au";
    }
    if (countryKey === "new_zealand" && disciplineKey === "business_management") {
        return "dream_university_fm_discipline_business_management_nz";
    }
    if (
        (countryKey === "united_kingdom" || countryKey === "ireland")
        && disciplineKey === "business_management"
    ) {
        return "dream_university_fm_discipline_business_management_uk_ie";
    }
    if (countryKey === "united_states" && disciplineKey === "business_management") {
        return "dream_university_fm_discipline_business_management_short";
    }
    return `dream_university_fm_discipline_${disciplineKey}`;
}

/** Country-aware discipline grouping. Ireland lists Podiatry and Occupational
 *  Therapy under Health & Life Sciences instead of the default Allied Medical
 *  grouping; every other country keeps the global mapping. */
function disciplineForDept(dept: Department): FmDisciplineKey | undefined {
    if (
        dept.departmentKey === "ireland"
        && (dept.collegeKey === "podiatry" || dept.collegeKey === "occupational_therapy")
    ) {
        return "health_life_sciences";
    }
    if (
        (dept.departmentKey === "ireland"
            || dept.departmentKey === "australia"
            || dept.departmentKey === "new_zealand")
        && dept.collegeKey === "agriculture_sciences"
    ) {
        return "agriculture";
    }
    if (dept.departmentKey === "australia" && dept.collegeKey === "product_design") {
        return "business_management";
    }
    if (
        (dept.departmentKey === "australia" || dept.departmentKey === "new_zealand")
        && dept.collegeKey === "biomedical_sciences"
    ) {
        return "allied_medical";
    }
    if (dept.departmentKey === "australia" && dept.collegeKey === "veterinary_medicine") {
        return "medicine_dentistry_pharmacy";
    }
    return specialtyToDiscipline[dept.collegeKey];
}

// ── Departments ─────────────────────────────────────────────────────────
// Each row = (country × specialty). `track` is "scientific" for sci-only
// rows in the PDF; sci-or-literary rows omit `track` (both tracks see them).

export const departments: Department[] = [
    // ── Medicine (طب بشري) ──────────────────────────────────────────
    { code: "FM-MED-UK", collegeKey: "medicine", departmentKey: "united_kingdom", equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 96.6305 },
    { code: "FM-MED-IE", collegeKey: "medicine", departmentKey: "ireland",        equivalentRate: 97, type: "high_school", track: "scientific", minAcceptedLastYear: 96.7425 },
    { code: "FM-MED-AU", collegeKey: "medicine", departmentKey: "australia",      equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 95.58 },
    { code: "FM-MED-NZ", collegeKey: "medicine", departmentKey: "new_zealand",    equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 95.86 },
    { code: "FM-MED-MT", collegeKey: "medicine", departmentKey: "malta",          equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 95.838 },
    { code: "FM-MED-BH", collegeKey: "medicine", departmentKey: "bahrain",        equivalentRate: 97, type: "high_school", track: "scientific", minAcceptedLastYear: 96.0885 },
    { code: "FM-MED-SA", collegeKey: "medicine", departmentKey: "saudi_arabia",   equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 96.0905 },

    // ── Dentistry (طب أسنان) ─────────────────────────────────────────
    { code: "FM-DEN-UK", collegeKey: "dentistry", departmentKey: "united_kingdom", equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 95.8015 },
    { code: "FM-DEN-IE", collegeKey: "dentistry", departmentKey: "ireland",        equivalentRate: 96, type: "high_school", track: "scientific", minAcceptedLastYear: 99.1165 },
    { code: "FM-DEN-AU", collegeKey: "dentistry", departmentKey: "australia",      equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 96.265 },
    { code: "FM-DEN-NZ", collegeKey: "dentistry", departmentKey: "new_zealand",    equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 95.433 },

    // ── Pharmacy (الصيدلة) ──────────────────────────────────────────
    { code: "FM-PHA-IE", collegeKey: "pharmacy", departmentKey: "ireland",      equivalentRate: 96, type: "high_school", track: "scientific", minAcceptedLastYear: 89.61 },
    { code: "FM-PHA-AU", collegeKey: "pharmacy", departmentKey: "australia",    equivalentRate: 92, type: "high_school", track: "scientific", minAcceptedLastYear: 90.69 },
    { code: "FM-PHA-NZ", collegeKey: "pharmacy", departmentKey: "new_zealand",  equivalentRate: 92, type: "high_school", track: "scientific", minAcceptedLastYear: 90.2815 },
    { code: "FM-PHA-BH", collegeKey: "pharmacy", departmentKey: "bahrain",      equivalentRate: 92, type: "high_school", track: "scientific", minAcceptedLastYear: 92.6135 },
    { code: "FM-PHA-SA", collegeKey: "pharmacy", departmentKey: "saudi_arabia", equivalentRate: 92, type: "high_school", track: "scientific", minAcceptedLastYear: 91.5165 },

    // ── Nursing (التمريض) ───────────────────────────────────────────
    { code: "FM-NUR-UK", collegeKey: "nursing", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 92.34 },
    { code: "FM-NUR-IE", collegeKey: "nursing", departmentKey: "ireland",        equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92.3 },
    { code: "FM-NUR-AU", collegeKey: "nursing", departmentKey: "australia",      equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.8 },
    { code: "FM-NUR-NZ", collegeKey: "nursing", departmentKey: "new_zealand",    equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 87.22 },
    { code: "FM-NUR-MT", collegeKey: "nursing", departmentKey: "malta",          equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 95.51 },
    { code: "FM-NUR-BH", collegeKey: "nursing", departmentKey: "bahrain",        equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 90 },
    { code: "FM-NUR-SA", collegeKey: "nursing", departmentKey: "saudi_arabia",   equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 94.66 },
    { code: "FM-NUR-AE", collegeKey: "nursing", departmentKey: "uae",            equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 90.23 },

    // ── Children Nursing ───────────────────────────────────────────
    { code: "FM-CHN-IE", collegeKey: "child_nursing", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 93.17 },

    // ── Mental Health Nursing (2025/2026 plan only) ────────────────
    { code: "FM-MPN-IE", collegeKey: "mental_health_nursing", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific" },

    // ── Intellectual Disability Nursing (2025/2026 plan only) ──────
    { code: "FM-IDN-IE", collegeKey: "intellectual_disability_nursing", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific" },

    // ── Advanced Therapeutic Technologies ──────────────────────────
    { code: "FM-ATT-IE", collegeKey: "advanced_therapeutic_tech", departmentKey: "ireland", equivalentRate: 92, type: "high_school", track: "scientific", minAcceptedLastYear: 95 },

    // ── Physiotherapy ──────────────────────────────────────────────
    { code: "FM-PTH-UK", collegeKey: "physiotherapy", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 96.64 },
    { code: "FM-PTH-IE", collegeKey: "physiotherapy", departmentKey: "ireland",        equivalentRate: 92, type: "high_school", track: "scientific", minAcceptedLastYear: 95.5 },
    { code: "FM-PTH-AU", collegeKey: "physiotherapy", departmentKey: "australia",      equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 95.25 },
    { code: "FM-PTH-NZ", collegeKey: "physiotherapy", departmentKey: "new_zealand",    equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 94.73 },
    { code: "FM-PTH-AE", collegeKey: "physiotherapy", departmentKey: "uae",            equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 97.12 },
    { code: "FM-PTH-SA", collegeKey: "physiotherapy", departmentKey: "saudi_arabia",   equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 98.3 },

    // ── Radiography ────────────────────────────────────────────────
    { code: "FM-RGR-US", collegeKey: "radiography", departmentKey: "united_states",  equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.3 },
    { code: "FM-RGR-UK", collegeKey: "radiography", departmentKey: "united_kingdom", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 93.81 },
    { code: "FM-RGR-IE", collegeKey: "radiography", departmentKey: "ireland",        equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 93.78 },

    // ── Radiation Therapy ──────────────────────────────────────────
    { code: "FM-RAD-IE", collegeKey: "radiation_therapy", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92 },

    // ── Diagnostic Radiology ───────────────────────────────────────
    { code: "FM-DRD-AU", collegeKey: "diagnostic_radiology", departmentKey: "australia", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 87.6 },
    { code: "FM-DRD-BH", collegeKey: "diagnostic_radiology", departmentKey: "bahrain",   equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 95.71 },

    // ── Medical Diagnostic Imaging ─────────────────────────────────
    { code: "FM-MDI-AE", collegeKey: "medical_diagnostic_imaging", departmentKey: "uae", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 94.2 },

    // ── Radiology Sciences ─────────────────────────────────────────
    { code: "FM-RDS-SA", collegeKey: "radiology_sciences", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 94 },

    // ── Computer Science ───────────────────────────────────────────
    { code: "FM-CSC-US", collegeKey: "computer_science", departmentKey: "united_states",  equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85 },
    { code: "FM-CSC-UK", collegeKey: "computer_science", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 88.94 },
    { code: "FM-CSC-IE", collegeKey: "computer_science", departmentKey: "ireland",        equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.63 },
    { code: "FM-CSC-AU", collegeKey: "computer_science", departmentKey: "australia",      equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.2 },

    // ── CS / Cyber Security ────────────────────────────────────────
    { code: "FM-CSCY-UK", collegeKey: "computer_science_cyber_security", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 92.8 },

    // ── CS / AI ────────────────────────────────────────────────────
    { code: "FM-CSAI-UK", collegeKey: "computer_science_ai", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 89.36 },

    // ── Cyber Security ─────────────────────────────────────────────
    { code: "FM-CYB-AU", collegeKey: "cybersecurity", departmentKey: "australia",   equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.94 },
    { code: "FM-CYB-NZ", collegeKey: "cybersecurity", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.71 },

    // ── Cybersecurity Engineering ──────────────────────────────────
    { code: "FM-CSE-US", collegeKey: "cybersecurity_engineering", departmentKey: "united_states", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 67.2895 },

    // ── Cyber Security & IT Forensics ──────────────────────────────
    { code: "FM-CYIF-IE", collegeKey: "cyber_security_it_forensics", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 91.92 },

    // ── Computer Engineering ───────────────────────────────────────
    { code: "FM-CE-US", collegeKey: "computer_engineering", departmentKey: "united_states", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 70.3515 },
    { code: "FM-CE-AE", collegeKey: "computer_engineering", departmentKey: "uae",           equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 75.64 },

    // ── Computer & Network Engineering ─────────────────────────────
    { code: "FM-CNE-AU", collegeKey: "computer_network_engineering", departmentKey: "australia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 75.961 },

    // ── Pharmacology ───────────────────────────────────────────────
    { code: "FM-PHL-UK", collegeKey: "pharmacology", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 94.72 },
    { code: "FM-PHL-AU", collegeKey: "pharmacology", departmentKey: "australia",      equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 88.94 },

    // ── Food Science ───────────────────────────────────────────────
    { code: "FM-FSC-UK", collegeKey: "food_science", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.45 },

    // ── Nutrition Sciences ─────────────────────────────────────────
    { code: "FM-NUT-UK", collegeKey: "nutrition_sciences", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 92.22 },

    // ── Food & Human Nutrition (علوم التغذية) ────────────────────────
    { code: "FM-FHN-AU", collegeKey: "food_human_nutrition", departmentKey: "australia", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86 },

    // ── Food Processing Engineering (2025/2026 plan only) ──────────
    { code: "FM-FPE-NZ", collegeKey: "food_processing_engineering", departmentKey: "new_zealand", equivalentRate: 90, type: "high_school", track: "scientific" },

    // ── Actuarial Science ──────────────────────────────────────────
    { code: "FM-ACS-UK", collegeKey: "actuarial_sciences", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85 },

    // ── Actuarial & Financial Studies ──────────────────────────────
    { code: "FM-AFS-IE", collegeKey: "actuarial_financial_studies", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.4 },

    // ── Data Science ───────────────────────────────────────────────
    { code: "FM-DS-UK", collegeKey: "data_science", departmentKey: "united_kingdom", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.84 },
    { code: "FM-DS-IE", collegeKey: "data_science", departmentKey: "ireland",        equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.91 },
    { code: "FM-DS-AU", collegeKey: "data_science", departmentKey: "australia",      equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.2 },
    { code: "FM-DS-NZ", collegeKey: "data_science", departmentKey: "new_zealand",    equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 80.43 },

    // ── Statistics (2025/2026 plan only) ───────────────────────────
    { code: "FM-STA-US", collegeKey: "statistics", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.08 },
    { code: "FM-STA-AU", collegeKey: "statistics", departmentKey: "australia",     equivalentRate: 85, type: "high_school", track: "scientific" },

    // ── Biochemistry ───────────────────────────────────────────────
    { code: "FM-BCH-US", collegeKey: "biochemistry", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 89.12 },

    // ── Biochemistry & Molecular Biology ───────────────────────────
    { code: "FM-BMB-IE", collegeKey: "biochemistry_molecular_biology", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 88.18 },

    // ── Cell & Molecular Biology ───────────────────────────────────
    { code: "FM-CMB-IE", collegeKey: "cell_molecular_biology", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 87.99 },

    // ── Microbiology ───────────────────────────────────────────────
    { code: "FM-MCR-IE", collegeKey: "microbiology", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.76 },

    // ── Physics ────────────────────────────────────────────────────
    { code: "FM-PHY-US", collegeKey: "physics", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.8 },

    // ── Chemistry (2025/2026 plan only) ────────────────────────────
    { code: "FM-CHE-US", collegeKey: "chemistry", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 83.53 },

    // ── Biology ────────────────────────────────────────────────────
    { code: "FM-BIO-US", collegeKey: "biology", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 90.2 },

    // ── Mathematics ────────────────────────────────────────────────
    { code: "FM-MTH-US", collegeKey: "mathematics", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 87.96 },

    // ── Architecture ───────────────────────────────────────────────
    { code: "FM-ARC-US", collegeKey: "architecture", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.45 },

    // ── Architectural Engineering ──────────────────────────────────
    { code: "FM-ARCH-US", collegeKey: "architectural_engineering", departmentKey: "united_states", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 73.451 },

    // ── Architectural Science ──────────────────────────────────────
    { code: "FM-ARS-IE", collegeKey: "architectural_science", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.84 },

    // ── Landscape Architecture ─────────────────────────────────────
    { code: "FM-LAN-US", collegeKey: "landscape_architecture", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 89.58 },

    // ── Urban / City Planning (2025/2026 plan only) ────────────────
    { code: "FM-URP-US", collegeKey: "urban_planning", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.86 },

    // ── Graphic Design (sci-or-literary in USA) ────────────────────
    { code: "FM-GRA-US", collegeKey: "graphic_design", departmentKey: "united_states", equivalentRate: 85, type: "high_school", minAcceptedLastYear: 85.4 },

    // ── English Literature (sci-or-literary in USA & AU) ───────────
    { code: "FM-ENG-US", collegeKey: "english_literature", departmentKey: "united_states", equivalentRate: 85, type: "high_school", minAcceptedLastYear: 86.5 },
    { code: "FM-ENG-AU", collegeKey: "english_literature", departmentKey: "australia",     equivalentRate: 85, type: "high_school", minAcceptedLastYear: 85.54 },

    // ── Entrepreneurship (sci-or-literary in USA) ──────────────────
    { code: "FM-ENT-US", collegeKey: "entrepreneurship", departmentKey: "united_states", equivalentRate: 85, type: "high_school", minAcceptedLastYear: 85 },

    // ── Supply Chain Management (sci-or-literary in USA) ───────────
    { code: "FM-SCM-US", collegeKey: "supply_chain_management", departmentKey: "united_states", equivalentRate: 85, type: "high_school", minAcceptedLastYear: 85.28 },

    // ── Biomedical Engineering ─────────────────────────────────────
    { code: "FM-BME-US", collegeKey: "biomedical_engineering", departmentKey: "united_states", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 59.553 },
    { code: "FM-BME-IE", collegeKey: "biomedical_engineering", departmentKey: "ireland",       equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 79.812 },
    { code: "FM-BME-AU", collegeKey: "biomedical_engineering", departmentKey: "australia",     equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 77.045 },

    // ── Environmental Engineering ──────────────────────────────────
    { code: "FM-ENV-US", collegeKey: "environmental_engineering", departmentKey: "united_states", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 68.046 },

    // ── Renewable Energy & Electrical Engineering ──────────────────
    { code: "FM-REE-AU", collegeKey: "renewable_energy_engineering", departmentKey: "australia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 79.172 },

    // ── Software Engineering ───────────────────────────────────────
    { code: "FM-SWE-AU", collegeKey: "software_engineering", departmentKey: "australia",   equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 77.5645 },
    { code: "FM-SWE-NZ", collegeKey: "software_engineering", departmentKey: "new_zealand", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92.8195 },

    // ── Materials Engineering (2025/2026 plan only) ────────────────
    { code: "FM-MAT-AU", collegeKey: "materials_engineering", departmentKey: "australia", equivalentRate: 90, type: "high_school", track: "scientific" },

    // ── Mechatronics Engineering ───────────────────────────────────
    { code: "FM-MCT-AU", collegeKey: "mechatronics_engineering", departmentKey: "australia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 83.77 },

    // ── Surveying Engineering ──────────────────────────────────────
    { code: "FM-SVE-AU", collegeKey: "surveying_engineering", departmentKey: "australia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 76.602 },

    // ── Biomedical Health & Life Sciences ──────────────────────────
    { code: "FM-BHLS-IE", collegeKey: "biomedical_health_life_sciences", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 91.57 },

    // ── Biomedical Sciences ────────────────────────────────────────
    { code: "FM-BMS-AU", collegeKey: "biomedical_sciences", departmentKey: "australia",   equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.07 },
    { code: "FM-BMS-NZ", collegeKey: "biomedical_sciences", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.4 },

    // ── Biomedical Technology ──────────────────────────────────────
    { code: "FM-BMT-SA", collegeKey: "biomedical_technology", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 90.42 },

    // ── Human Health & Diseases (2025/2026 plan only) ──────────────
    { code: "FM-HHD-IE", collegeKey: "human_health_disease", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific" },

    // ── Health & Performance Science (2025/2026 plan only) ─────────
    { code: "FM-HPS-IE", collegeKey: "health_performance_science", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific" },

    // ── Sport Science & Health ─────────────────────────────────────
    { code: "FM-SPH-IE", collegeKey: "sports_health_sciences", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92.4 },

    // ── Community Health Sciences ──────────────────────────────────
    { code: "FM-CHS-SA", collegeKey: "community_health_sciences", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 95.17 },

    // ── Podiatry ───────────────────────────────────────────────────
    { code: "FM-POD-IE", collegeKey: "podiatry", departmentKey: "ireland",     equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 90.88 },
    { code: "FM-POD-AU", collegeKey: "podiatry", departmentKey: "australia",   equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 87.8 },
    { code: "FM-POD-NZ", collegeKey: "podiatry", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.58 },
    { code: "FM-POD-MT", collegeKey: "podiatry", departmentKey: "malta",       equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 93.86 },

    // ── Occupational Therapy ───────────────────────────────────────
    { code: "FM-OTH-IE", collegeKey: "occupational_therapy", departmentKey: "ireland",   equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92.79 },
    { code: "FM-OTH-AU", collegeKey: "occupational_therapy", departmentKey: "australia", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 89.81 },

    // ── Human Nutrition (2025/2026 plan only) ──────────────────────
    { code: "FM-HNU-IE", collegeKey: "human_nutrition", departmentKey: "ireland", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 93.04 },

    // ── Clinical Nutrition — KSA ───────────────────────────────────
    { code: "FM-CLN-SA", collegeKey: "clinical_nutrition_sa", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 91.23 },

    // ── Clinical Nutrition & Dietetics — UAE ───────────────────────
    { code: "FM-CLN-AE", collegeKey: "clinical_nutrition_ae", departmentKey: "uae", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 90.27 },

    // ── Clinical Laboratory Sciences ───────────────────────────────
    { code: "FM-CLS-SA", collegeKey: "clinical_lab_sciences", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92.28 },

    // ── Medical Laboratory Science ─────────────────────────────────
    { code: "FM-MLS-US", collegeKey: "medical_lab_science", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.04 },
    { code: "FM-MLS-AU", collegeKey: "medical_lab_science", departmentKey: "australia",     equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.73 },
    { code: "FM-MLS-NZ", collegeKey: "medical_lab_science", departmentKey: "new_zealand",   equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 91.92 },
    { code: "FM-MLS-BH", collegeKey: "medical_lab_science", departmentKey: "bahrain",       equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 94.91 },
    { code: "FM-MLS-AE", collegeKey: "medical_lab_science", departmentKey: "uae",           equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 90.92 },

    // ── Emergency Medical Services ─────────────────────────────────
    { code: "FM-EMS-SA", collegeKey: "emergency_medical_services", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 90.73 },

    // ── Speech Therapy / Pathology ─────────────────────────────────
    { code: "FM-SLT-AU", collegeKey: "speech_therapy", departmentKey: "australia",    equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 89.02 },
    { code: "FM-SLT-SA", collegeKey: "speech_therapy", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92.39 },

    // ── Optometry ──────────────────────────────────────────────────
    { code: "FM-OPT-NZ", collegeKey: "optics", departmentKey: "new_zealand",  equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 88.89 },
    { code: "FM-OPT-SA", collegeKey: "optics", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 90.01 },

    // ── Paramedic (طوارئ طبية — NZ) ──────────────────────────────────
    { code: "FM-EMR-NZ", collegeKey: "paramedics", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 90.44 },

    // ── Paramedic (طوارئ طبية — AU) ──────────────────────────────────
    { code: "FM-EMRH-AU", collegeKey: "paramedic_honours", departmentKey: "australia", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.63 },

    // ── Dental Technology ──────────────────────────────────────────
    { code: "FM-DTH-NZ", collegeKey: "dental_technology", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 95.7 },

    // ── Dental Care ────────────────────────────────────────────────
    { code: "FM-DCA-SA", collegeKey: "dental_care", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 90.27 },

    // ── Oral Health (NZ: 2025/2026 plan only) ──────────────────────
    { code: "FM-OHL-AU", collegeKey: "oral_health", departmentKey: "australia",   equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 92.53 },
    { code: "FM-OHL-NZ", collegeKey: "oral_health", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 80.22 },

    // ── Veterinary Medicine ────────────────────────────────────────
    { code: "FM-VET-AU", collegeKey: "veterinary_medicine", departmentKey: "australia", equivalentRate: 95, type: "high_school", track: "scientific", minAcceptedLastYear: 93.3615 },

    // ── Respiratory Therapy ────────────────────────────────────────
    { code: "FM-RSP-SA", collegeKey: "respiratory_therapy", departmentKey: "saudi_arabia", equivalentRate: 90, type: "high_school", track: "scientific", minAcceptedLastYear: 96.92 },

    // ── Kinesiology ────────────────────────────────────────────────
    { code: "FM-KIN-US", collegeKey: "kinesiology", departmentKey: "united_states", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.45 },

    // ── Marine Biology (علوم بحرية) ───────────────────────────────────
    { code: "FM-MRB-AU", collegeKey: "marine_biology", departmentKey: "australia", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.21 },

    // ── Immunology & Pathology ─────────────────────────────────────
    { code: "FM-IMP-AU", collegeKey: "immunology_pathology", departmentKey: "australia", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 86.41 },

    // ── Construction Management (sci-or-literary in AU) ────────────
    { code: "FM-CMG-AU", collegeKey: "construction_management", departmentKey: "australia", equivalentRate: 85, type: "high_school", minAcceptedLastYear: 88.36 },

    // ── Tourism & Hospitality (sci-or-literary; 2025/2026 plan only) ─
    { code: "FM-THM-AU", collegeKey: "tourism_hospitality", departmentKey: "australia",   equivalentRate: 85, type: "high_school", minAcceptedLastYear: 80.11 },
    { code: "FM-THM-NZ", collegeKey: "tourism_hospitality", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school" },

    // ── Product Design (sci-or-literary in AU) ─────────────────────
    { code: "FM-PRD-AU", collegeKey: "product_design", departmentKey: "australia", equivalentRate: 85, type: "high_school", minAcceptedLastYear: 87.68 },

    // ── Computer Game Development (2025/2026 plan only) ────────────
    { code: "FM-CGD-IE", collegeKey: "computer_game_dev", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific" },

    // ── Multimedia (2025/2026 plan only) ───────────────────────────
    { code: "FM-MUL-IE", collegeKey: "multimedia", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific" },

    // ── Artificial Intelligence ────────────────────────────────────
    { code: "FM-AI-AU", collegeKey: "artificial_intelligence", departmentKey: "australia",   equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.1 },
    { code: "FM-AI-NZ", collegeKey: "artificial_intelligence", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.2 },

    // ── Agricultural Sciences (2025/2026 plan only) ────────────────
    { code: "FM-AGR-IE", collegeKey: "agriculture_sciences", departmentKey: "ireland",     equivalentRate: 85, type: "high_school", track: "scientific" },
    { code: "FM-AGR-AU", collegeKey: "agriculture_sciences", departmentKey: "australia",   equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 81.97 },
    { code: "FM-AGR-NZ", collegeKey: "agriculture_sciences", departmentKey: "new_zealand", equivalentRate: 85, type: "high_school", track: "scientific" },

    // ── Technology Management ──────────────────────────────────────
    { code: "FM-TMG-IE", collegeKey: "technology_management", departmentKey: "ireland", equivalentRate: 85, type: "high_school", track: "scientific", minAcceptedLastYear: 85.16 },
];

// ── Colleges — one entry per specialty (collegeKey) ─────────────────────
// Weights: medicine/dentistry/pharmacy and all engineering use weighted
// formula; everything else is HS-only.

function pickWeights(collegeKey: string): CollegeWeights {
    if (collegeKey === "medicine" || collegeKey === "dentistry" || collegeKey === "veterinary_medicine") return fmMedicineDentistry;
    if (collegeKey === "pharmacy") return fmPharmacy;
    if (specialtyToDiscipline[collegeKey] === "engineering") return fmEngineering;
    return fmHsOnly;
}

const collegeKeys = Array.from(new Set(departments.map((d) => d.collegeKey)));
collegeKeys.sort();

export const colleges: College[] = collegeKeys.map((key) => ({
    key,
    emoji: "🎓",
    weights: pickWeights(key),
    departments: departments.filter((d) => d.collegeKey === key),
    nameKey: key === "medicine" ? "dream_university_fm_college_medicine" : undefined,
}));

/** 2025/2026-plan offerings kept although the 2026/2027 plan dropped them.
 *  Their display name carries a "(2025/2026)" suffix via the dedicated
 *  dream_university_fm_2025_2026_* label keys. */
export const fm2025LegacyCodes = new Set([
    "FM-STA-US", "FM-CHE-US", "FM-URP-US",
    "FM-MPN-IE", "FM-IDN-IE", "FM-HHD-IE", "FM-HPS-IE", "FM-HNU-IE", "FM-CGD-IE", "FM-MUL-IE", "FM-AGR-IE",
    "FM-STA-AU", "FM-MAT-AU", "FM-THM-AU", "FM-AGR-AU",
    "FM-DS-NZ", "FM-OHL-NZ", "FM-THM-NZ", "FM-AGR-NZ", "FM-FPE-NZ",
]);

/** FM specialty display-name key. Most specialties use the college's
 *  nameKey/default, but a few (specialty, country) pairs override it — e.g.
 *  Saudi Arabia's Pharmacy is "Doctor of Pharmacy" (دكتور في الصيدلة) while
 *  every other country keeps the plain "Pharmacy" label. */
export function getFmSpecialtyNameKey(dept: Department): string {
    if (fm2025LegacyCodes.has(dept.code)) {
        return `dream_university_fm_2025_2026_${dept.collegeKey}`;
    }
    if (dept.collegeKey === "pharmacy" && dept.departmentKey === "saudi_arabia") {
        return "dream_university_fm_college_pharmacy";
    }
    if (dept.collegeKey === "computer_science" && dept.departmentKey === "united_states") {
        return "dream_university_fm_computer_science_usa";
    }
    if (
        dept.collegeKey === "radiography"
        && (dept.departmentKey === "united_states" || dept.departmentKey === "ireland")
    ) {
        return "dream_university_fm_radiography_usa";
    }
    if (dept.collegeKey === "computer_science" && dept.departmentKey === "australia") {
        return "dream_university_fm_computer_science_au";
    }
    if (dept.collegeKey === "paramedic_honours" && dept.departmentKey === "australia") {
        return "dream_university_fm_paramedic_honours_au";
    }
    if (dept.collegeKey === "speech_therapy" && dept.departmentKey === "australia") {
        return "dream_university_fm_speech_therapy_au";
    }
    if (dept.collegeKey === "dental_technology" && dept.departmentKey === "new_zealand") {
        return "dream_university_fm_dental_technology_nz";
    }
    if (dept.collegeKey === "emergency_medical_services" && dept.departmentKey === "saudi_arabia") {
        return "dream_university_fm_emergency_medical_services_sa";
    }
    if (dept.collegeKey === "clinical_lab_sciences" && dept.departmentKey === "saudi_arabia") {
        return "dream_university_fm_clinical_lab_sciences_sa";
    }
    if (dept.collegeKey === "computer_engineering" && dept.departmentKey === "uae") {
        return "dream_university_fm_computer_engineering_ae";
    }
    if (dept.collegeKey === "entrepreneurship" && dept.departmentKey === "united_states") {
        return "dream_university_fm_entrepreneurship_usa";
    }
    if (dept.collegeKey === "computer_science_ai" && dept.departmentKey === "united_kingdom") {
        return "dream_university_fm_computer_science_ai_uk";
    }
    if (dept.collegeKey === "food_science" && dept.departmentKey === "united_kingdom") {
        return "dream_university_fm_food_science_uk";
    }
    if (dept.collegeKey === "nutrition_sciences" && dept.departmentKey === "united_kingdom") {
        return "dream_university_fm_nutrition_sciences_uk";
    }
    if (dept.collegeKey === "biomedical_health_life_sciences" && dept.departmentKey === "ireland") {
        return "dream_university_fm_biomedical_health_life_sciences_ie";
    }
    if (dept.collegeKey === "cyber_security_it_forensics" && dept.departmentKey === "ireland") {
        return "dream_university_fm_cyber_security_it_forensics_ie";
    }
    if (dept.collegeKey === "computer_network_engineering" && dept.departmentKey === "australia") {
        return "dream_university_fm_computer_network_engineering_au";
    }
    if (dept.collegeKey === "marine_biology" && dept.departmentKey === "australia") {
        return "dream_university_fm_marine_biology_au";
    }
    if (dept.collegeKey === "food_human_nutrition" && dept.departmentKey === "australia") {
        return "dream_university_fm_food_human_nutrition_au";
    }
    const college = colleges.find((c) => c.key === dept.collegeKey);
    return college ? getCollegeNameKey(college) : `dream_university_college_${dept.collegeKey}`;
}

// ── Module helpers ──────────────────────────────────────────────────────

/**
 * True when a specialty uses the HS-only weights template (no math/english
 * inputs). For these specialties the "last-year accepted" value is just a
 * HS percentage — calling it "equivalent rate" is misleading, so the UI
 * suppresses that line on the specialty card.
 */
export function isFmHsOnly(collegeKey: string): boolean {
    return pickWeights(collegeKey).admissionType === "high_school";
}

/** FM enforces each specialty's HS minimum as a hard apply-floor. */
export function isCollegeMinHardGate(): boolean {
    return true;
}

export function getCollegeMinRate(
    collegeKey: string,
    _gender?: Gender,
    _track?: Track | null,
): number {
    const collegeDepts = departments.filter((d) => d.collegeKey === collegeKey && d.equivalentRate > 0);
    if (collegeDepts.length === 0) return 0;
    return Math.min(...collegeDepts.map((d) => d.equivalentRate));
}

export function getRequiredFields(weights: CollegeWeights): string[] {
    const fields: string[] = ["high_school"];
    if (weights.englishWeight > 0) fields.push("english");
    if (weights.mathWeight > 0) fields.push("math");
    if (weights.arabicWeight > 0) fields.push("arabic");
    if (weights.chemistryWeight > 0) fields.push("chemistry");
    return fields;
}

export function getCollegeSpecialties(
    collegeKey: string,
    _gender?: Gender,
    track?: Track | null,
): Department[] {
    return departments.filter(
        (dept, index, self) =>
            dept.collegeKey === collegeKey &&
            dept.departmentKey !== null &&
            (!track || !dept.track || dept.track === track) &&
            self.findIndex(
                (d) => d.departmentKey === dept.departmentKey && d.collegeKey === collegeKey,
            ) === index,
    );
}

export function hasSelectableSpecialties(
    collegeKey: string,
    _gender?: Gender,
    track?: Track | null,
): boolean {
    return getCollegeSpecialties(collegeKey, undefined, track).length >= 1;
}

/* ── FM equivalent-rate calculation (isolated) ───────────────────────────
 * Medicine/Dentistry/Pharmacy and all Engineering colleges return the
 * weighted formula; everything else (admissionType === "high_school")
 * returns the raw HS percentage so no math/english inputs are required.
 */
export function calculateEquivalentRate(weights: CollegeWeights, scores: Scores): number {
    if (weights.admissionType === "high_school") {
        return scores.high_school;
    }
    const rate =
        (scores.high_school * weights.highSchoolWeight) / 100 +
        ((scores.english || 0) * weights.englishWeight) / 100 +
        ((scores.math || 0) * weights.mathWeight) / 100 +
        ((scores.arabic || 0) * weights.arabicWeight) / 100 +
        ((scores.chemistry || 0) * weights.chemistryWeight) / 100;
    return Math.round(rate * 100) / 100;
}

export function meetsMinHighSchool(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minHighSchool === null) return true;
    return scores.high_school >= weights.minHighSchool;
}

export function meetsMinEnglish(weights: CollegeWeights, scores: Scores): boolean {
    if (weights.minEnglish === null) return true;
    return (scores.english || 0) >= weights.minEnglish;
}

/* ── Per-section public API (consumed via the registry) ─────────────────
 * FM owns its country/discipline/track navigation, threshold, and label key.
 */

/** Foreign Missions does not impose a track-level HS minimum (each specialty
 *  has its own apply-floor). */
export function getTrackMinHighSchool(_track: Track | null | undefined): number | null {
    return null;
}

export function getDepartmentThreshold(dept: Department): number {
    return dept.minAcceptedLastYear ?? dept.equivalentRate;
}

export function getDisplayedMinRate(
    dept: Department | null,
    _track: Track | null | undefined,
    _weights: CollegeWeights,
    fallbackMinRate: number,
): number {
    if (!dept) return fallbackMinRate;
    return dept.minAcceptedLastYear ?? dept.equivalentRate;
}

export function getDisplayedMinRateLabelKey(weights: CollegeWeights): string {
    return weights.admissionType === "high_school"
        ? "dream_university_fm_last_year_hs_rate"
        : "dream_university_last_year_rate";
}

/** When the selected specialty has no published last-year accepted rate, FM
 *  shows an info box saying so. */
export function getScoreInfoAlert(
    _college: College,
    department: Department | null,
    _track: Track | null | undefined,
    _collegeMinHs: number | null,
    _enteredHs: number,
): StepAlert | null {
    if (department?.minAcceptedLastYear !== undefined) {
        return null;
    }
    return {
        severity: "info",
        lines: [{ key: "dream_university_fm_lowest_rate_not_announced" }],
    };
}

/** FM has no track minimum, so the results failure block is never reached;
 *  the generic lines are provided for completeness. */
export function getResultsFailureLines(
    college: College,
    _department: Department | null,
    _track: Track | null | undefined,
    trackMinHs: number | null,
    minRate: number,
): StepAlertLine[] {
    return [
        {
            key: "dream_university_below_accepted_rate_failure_line1",
            params: { minHs: college.weights.minHighSchool ?? trackMinHs ?? 0 },
        },
        {
            key: "dream_university_below_accepted_rate_failure_line2",
            params: { acceptedRate: minRate },
        },
    ];
}

export function getColleges(_gender?: Gender, _track?: Track | null): College[] {
    return colleges;
}

export function getDepartmentsForFilters(_gender?: Gender, _track?: Track | null): Department[] {
    return departments;
}

// ── FM-only navigation helpers (Track → Country → College → Specialty) ──

/** Countries that have at least one specialty for the given track. PDF
 *  order preserved. */
export function getFmCountriesForTrack(track: Track | null | undefined): FmCountry[] {
    const matchesTrack = (d: Department): boolean =>
        !track || !d.track || d.track === track;
    return fmCountries
        .filter((c) => departments.some((d) => d.departmentKey === c.key && matchesTrack(d)))
        .sort((a, b) => a.order - b.order);
}

/** Disciplines (colleges) available in the chosen country+track. */
export function getFmCollegesForCountry(
    countryKey: string,
    track: Track | null | undefined,
): FmDiscipline[] {
    const matchesTrack = (d: Department): boolean =>
        !track || !d.track || d.track === track;
    const presentDisciplines = new Set<FmDisciplineKey>();
    for (const d of departments) {
        if (d.departmentKey !== countryKey) continue;
        if (!matchesTrack(d)) continue;
        const disc = disciplineForDept(d);
        if (disc) presentDisciplines.add(disc);
    }
    return fmDisciplines
        .filter((dx) => presentDisciplines.has(dx.key))
        .sort((a, b) => a.order - b.order);
}

/** Specialty rows for a given (country, discipline, track). Each row is a
 *  Department whose `collegeKey` identifies the specialty. */
export function getFmSpecialtiesForCountryAndCollege(
    countryKey: string,
    discipline: FmDisciplineKey,
    track: Track | null | undefined,
): Department[] {
    const matchesTrack = (d: Department): boolean =>
        !track || !d.track || d.track === track;
    return departments
        .filter(
            (d) =>
                d.departmentKey === countryKey
                && matchesTrack(d)
                && disciplineForDept(d) === discipline,
        );
}
